import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { recordOwnReachActiveDay } from '@/data/milestones';
import { shouldRecordActiveDay } from '@/utils/milestones';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { MILESTONE_PRESENTATION } from '@/config/milestones';

const storageKey = (accountID, organizationID) => `@milestones/activeDay:${accountID}:${organizationID}`;

/**
 * Own-day active tracking (§4.12): calls record_own_reach_active_day at
 * most once per local device day, on mount, on every AppState→active
 * transition, and on a 5-minute timer while foregrounded so a midnight
 * rollover is caught without reopening the app.
 *
 * Throttling is a client convenience only — the server dedups the same
 * (organizationID, personID, localDate) and derives the date itself from
 * the organization timezone; the client never submits a person or date.
 *
 * @param {object} params
 * @param {boolean} params.enabled - flag + authenticated + not kiosk/emulating.
 * @param {string|null} params.accountID
 * @param {string|null} params.organizationID
 * @param {string|null} params.personID - used to invalidate own badge state
 *   when a genuinely new day was recorded.
 */
const useMilestoneActiveDay = ({ enabled, accountID, organizationID, personID } = {}) => {
    const queryClient = useQueryClient();
    const inFlight = useRef(false);

    useEffect(() => {
        if (!enabled || !accountID || !organizationID) return undefined;

        let cancelled = false;
        const key = storageKey(accountID, organizationID);

        const tick = async () => {
            if (cancelled || inFlight.current) return;
            // Never record from a hidden/background client (§4.12).
            if (AppState.currentState !== 'active') return;
            inFlight.current = true;
            try {
                const todayDeviceDate = DateTime.local().toISODate();
                const raw = await AsyncStorage.getItem(key);
                const saved = raw ? JSON.parse(raw) : null;
                if (!shouldRecordActiveDay(saved, todayDeviceDate)) return;

                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
                // Stamp the attempt first: a failed write must not retry-storm.
                await AsyncStorage.setItem(key, JSON.stringify({
                    attemptedDeviceDate: todayDeviceDate,
                    recordedLocalDate: saved?.recordedLocalDate ?? null,
                    timezone,
                }));

                const result = await recordOwnReachActiveDay();
                if (cancelled) return;
                await AsyncStorage.setItem(key, JSON.stringify({
                    attemptedDeviceDate: todayDeviceDate,
                    recordedLocalDate: result?.localDate ?? saved?.recordedLocalDate ?? null,
                    timezone,
                }));

                // A genuinely new day may have just earned progress — refresh
                // own badge state only (§12: own-day writes invalidate only
                // active-days progress).
                if (result?.recorded && personID) {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.milestones.person(organizationID, accountID, personID) });
                }
            } catch {
                // The adapter already routed unexpected errors to ErrorHandler;
                // a missed day is recoverable on the next foreground/date change.
            } finally {
                inFlight.current = false;
            }
        };

        tick();
        const subscription = AppState.addEventListener('change', (state) => {
            if (state === 'active') tick();
        });
        const interval = setInterval(tick, MILESTONE_PRESENTATION.activeDayRecheckMs);

        return () => {
            cancelled = true;
            subscription.remove();
            clearInterval(interval);
        };
    }, [enabled, accountID, organizationID, personID, queryClient]);
};

export default useMilestoneActiveDay;
